import { useState, useCallback, memo } from "react"
import { useMutation, useQuery } from "@apollo/client"
import {
  Badge,
  Box,
  Button,
  HStack,
  NativeSelect,
  Stack,
  Table,
  Text,
  VStack,
  createListCollection,
} from "@chakra-ui/react"
import { FaPlus, FaToggleOn, FaToggleOff, FaChartBar } from "react-icons/fa6"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { CancelButton, CreateButton, UpdateButton } from "@hydrologiq/shared/content/components/Button"
import {
  GetAllTenantsDocument,
  GetOrganizationTenantsDocument,
  AddTenantToOrganizationDocument,
  EnableTenantForOrganizationDocument,
  DisableTenantForOrganizationDocument,
  UpdateOrganizationStatusDocument,
} from "@hydrologiq/gql-experience/graphql"
import { ManageLimitsDialog } from "./ManageLimitsDialog"

interface OrganizationTenant {
  id: string
  name: string
  enabled: boolean
}

interface Organization {
  id: string
  name: string
  slug: string
  enabled: boolean
  tenants: OrganizationTenant[]
  seatLimit?: number | null
  memberCount: number
  createdAt: string
}

interface AdvancedOrganizationSettingsProps {
  organization: Organization
  onClose: () => void
  onUpdate: () => void
}

// Memoized dialog content for adding tenant
const AddTenantDialogContent = memo<{
  organizationId: string
  existingTenantIds: string[]
  allTenants: Array<{ id: string; name: string }>
  adding: boolean
  onSubmit: (tenantId: string) => void
  onCancel: () => void
}>(({ organizationId, existingTenantIds, allTenants, adding, onSubmit, onCancel }) => {
  const [tenantId, setTenantId] = useState("")

  const availableTenants = allTenants.filter((t) => !existingTenantIds.includes(t.id))

  const tenantCollection = createListCollection({
    items: [{ label: "Select a tenant", value: "" }, ...availableTenants.map((t) => ({ label: t.name, value: t.id }))],
  })

  const handleSubmit = useCallback(() => {
    if (!tenantId) {
      toaster.error({ title: "Please select a tenant" })
      return
    }
    onSubmit(tenantId)
  }, [tenantId, onSubmit])

  return (
    <VStack gap={4} align="stretch">
      <Text>Add a tenant to this organisation</Text>
      <Box>
        <Text fontSize="sm" mb={2}>
          Tenant
        </Text>
        <NativeSelect.Root>
          <NativeSelect.Field value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
            {tenantCollection.items.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </NativeSelect.Field>
        </NativeSelect.Root>
      </Box>
      <HStack>
        <CreateButton
          context={{ label: "Add Tenant", description: "Add a tenant to the organisation" }}
          onClick={handleSubmit}
          loading={adding}
        />
        <CancelButton
          context={{ label: "Cancel", description: "Cancel the add tenant operation" }}
          onClick={onCancel}
        />
      </HStack>
    </VStack>
  )
})

AddTenantDialogContent.displayName = "AddTenantDialogContent"

export const AdvancedOrganizationSettings = ({
  organization,
  onClose,
  onUpdate,
}: AdvancedOrganizationSettingsProps) => {
  const [showAddTenantDialog, setShowAddTenantDialog] = useState(false)
  const [showManageLimitsDialog, setShowManageLimitsDialog] = useState(false)

  const { data: tenantsData } = useQuery(GetAllTenantsDocument)
  const { data: orgTenantsData, refetch: refetchOrgTenants } = useQuery(GetOrganizationTenantsDocument, {
    variables: { organizationId: organization.id },
  })

  const [addTenant, { loading: adding }] = useMutation(AddTenantToOrganizationDocument, {
    onCompleted: () => {
      toaster.success({ title: "Tenant added to organisation" })
      setShowAddTenantDialog(false)
      refetchOrgTenants()
      onUpdate()
    },
    onError: (error) => {
      toaster.error({ title: "Failed to add tenant", description: error.message })
    },
  })

  const [enableTenant] = useMutation(EnableTenantForOrganizationDocument, {
    onCompleted: () => {
      toaster.success({ title: "Tenant enabled" })
      refetchOrgTenants()
      onUpdate()
    },
    onError: (error) => {
      toaster.error({ title: "Failed to enable tenant", description: error.message })
    },
  })

  const [disableTenant] = useMutation(DisableTenantForOrganizationDocument, {
    onCompleted: () => {
      toaster.success({ title: "Tenant disabled" })
      refetchOrgTenants()
      onUpdate()
    },
    onError: (error) => {
      toaster.error({ title: "Failed to disable tenant", description: error.message })
    },
  })

  const [updateOrgStatus] = useMutation(UpdateOrganizationStatusDocument, {
    onCompleted: () => {
      toaster.success({ title: "Organisation status updated" })
      onUpdate()
    },
    onError: (error) => {
      toaster.error({ title: "Failed to update organisation status", description: error.message })
    },
  })

  const handleAddTenant = useCallback(
    (tenantId: string) => {
      addTenant({
        variables: {
          organizationId: organization.id,
          tenantId,
        },
      })
    },
    [organization.id, addTenant]
  )

  const handleToggleTenant = useCallback(
    (tenant: OrganizationTenant) => {
      if (tenant.enabled) {
        // Check if this is the last enabled tenant
        const enabledCount = (orgTenantsData?.getOrganizationTenants || []).filter((t: any) => t.enabled).length
        if (enabledCount <= 1) {
          toaster.error({ title: "Cannot disable the last enabled tenant" })
          return
        }
        disableTenant({
          variables: {
            organizationId: organization.id,
            tenantId: tenant.id,
          },
        })
      } else {
        enableTenant({
          variables: {
            organizationId: organization.id,
            tenantId: tenant.id,
          },
        })
      }
    },
    [organization.id, orgTenantsData, enableTenant, disableTenant]
  )

  const handleToggleOrganization = useCallback(() => {
    updateOrgStatus({
      variables: {
        organizationId: organization.id,
        enabled: !organization.enabled,
      },
    })
  }, [organization.id, organization.enabled, updateOrgStatus])

  const allTenants = tenantsData?.getAllTenants || []
  const orgTenants = orgTenantsData?.getOrganizationTenants || organization.tenants

  return (
    <Stack gap={6}>
      {/* Organization Status */}
      <Box>
        <HStack justify="space-between" mb={2}>
          <Text fontSize="lg" fontWeight="bold">
            Organisation Status
          </Text>
          <UpdateButton
            context={{
              label: organization.enabled ? "Disable Organisation" : "Enable Organisation",
              description: organization.enabled ? "Disable the organisation" : "Enable the organisation",
            }}
            onClick={handleToggleOrganization}
          />
        </HStack>
        <Text fontSize="sm">
          {organization.enabled ? "Organisation is currently active" : "Organisation is currently disabled"}
        </Text>
      </Box>

      {/* Tenant Management */}
      <Box>
        <HStack justify="space-between" mb={4}>
          <Text fontSize="lg" fontWeight="bold">
            Tenant Management
          </Text>
          <CreateButton
            context={{ label: "Add Tenant", description: "Add a tenant to the organisation" }}
            onClick={() => setShowAddTenantDialog(true)}
          />
        </HStack>

        {orgTenants.length === 0 ? (
          <Text>No tenants linked to this organisation</Text>
        ) : (
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Tenant</Table.ColumnHeader>
                <Table.ColumnHeader>Status</Table.ColumnHeader>
                <Table.ColumnHeader>Actions</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {orgTenants.map((tenant: OrganizationTenant) => (
                <Table.Row key={tenant.id}>
                  <Table.Cell>
                    <Text fontWeight="medium">{tenant.name}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    {tenant.enabled ? (
                      <Badge colorPalette="green">Enabled</Badge>
                    ) : (
                      <Badge colorPalette="gray">Disabled</Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <UpdateButton
                      context={{
                        label: tenant.enabled ? "Disable Tenant" : "Enable Tenant",
                        description: tenant.enabled ? "Disable the tenant" : "Enable the tenant",
                        variant: tenant.enabled ? "warning" : "primary",
                      }}
                      onClick={() => handleToggleTenant(tenant)}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Box>

      {/* Manage Limits */}
      <Box>
        <HStack justify="space-between" mb={4}>
          <Box>
            <Text fontSize="lg" fontWeight="bold">
              Limits
            </Text>
            <Text fontSize="sm">Manage seat and usage limits for this organisation</Text>
          </Box>
          <UpdateButton
            context={{ label: "Manage Limits", description: "Manage seat and usage limits for the organisation" }}
            onClick={() => setShowManageLimitsDialog(true)}
          />
        </HStack>

        <Box p={4} borderWidth={1} borderRadius="md">
          <Text fontSize="sm">
            Seats: {organization.memberCount} / {organization.seatLimit || "Unlimited"}
          </Text>
        </Box>
      </Box>

      {/* Add Tenant Dialog */}
      <Dialog
        title="Add Tenant to Organisation"
        open={showAddTenantDialog}
        setOpen={setShowAddTenantDialog}
        loading={adding}
        disabled={adding}
      >
        <AddTenantDialogContent
          organizationId={organization.id}
          existingTenantIds={orgTenants.map((t: OrganizationTenant) => t.id)}
          allTenants={allTenants}
          adding={adding}
          onSubmit={handleAddTenant}
          onCancel={() => setShowAddTenantDialog(false)}
        />
      </Dialog>

      {/* Manage Limits Dialog */}
      {showManageLimitsDialog && (
        <Dialog title="Manage Limits" open={showManageLimitsDialog} setOpen={setShowManageLimitsDialog}>
          <ManageLimitsDialog
            organization={organization}
            onClose={() => setShowManageLimitsDialog(false)}
            onUpdate={onUpdate}
          />
        </Dialog>
      )}
    </Stack>
  )
}
